import { useAppSelector } from "../../app";
import {
  CategorizationWithConfig,
  BlockingCategorization,
} from "../../configMap/interfaces/category";
import selectCategorizations from "../selectors/selectCategorizations";
import { useMultipleGroupBlocks } from "../../configMap";
import { useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import {
  changeActiveButton,
  changeActiveEntity,
  changeAnnotating,
} from "../../app/store/appSlice";
import { setIncompleteCategorizations } from "../store/metadataSlice";

const useValidateBlockingCategorizations = () => {
  const dispatch = useDispatch();

  const categorizationsWithConfig = useAppSelector((state) =>
    selectCategorizations(state, { withConfig: true }),
  ) as CategorizationWithConfig[];

  const groupBlocks = useAppSelector(
    (state) => state.configMapReducer.configMap?.groupBlocks,
  );
  const categorizationAnnotations = useAppSelector(
    (state) =>
      state.metadataReducer.categorizationState?.categorizationAnnotations ||
      [],
  );
  const enforceRequiredCategorizations = useAppSelector(
    (state) =>
      state.documentReducer.activeDocument?.documentType
        ?.enforceRequiredCategorizations,
  );

  const { countPerGroupBlockId } = useMultipleGroupBlocks();

  const groupBlockCategorizationMap = useMemo(() => {
    if (!groupBlocks) {
      return new Map();
    }

    const map = new Map();
    groupBlocks.forEach((groupBlock) => {
      groupBlock.categorizationLinks
        ?.filter((link) => link.groupBlockId)
        ?.forEach((link) => {
          map.set(link.categorization.id, groupBlock.id);
        });
    });
    return map;
  }, [groupBlocks]);

  const getBlockingCategorizations = useCallback(() => {
    if (!categorizationsWithConfig || !enforceRequiredCategorizations) {
      return null;
    }

    return categorizationsWithConfig
      .filter(({ required }) => required)
      .flatMap((categorizationWithConfig) => {
        const groupBlockId = groupBlockCategorizationMap.get(
          categorizationWithConfig.categorization.id,
        );
        const groupBlockItemCount = groupBlockId
          ? countPerGroupBlockId(groupBlockId)
          : 1;

        return Array.from({ length: groupBlockItemCount }, (_, index) => ({
          categorizationId: categorizationWithConfig.categorization.id,
          groupBlockId,
          index: index + 1,
        }));
      });
  }, [
    categorizationsWithConfig,
    groupBlockCategorizationMap,
    countPerGroupBlockId,
    enforceRequiredCategorizations,
  ]);

  const setIncompleteCategorizationsAndScrollTo = useCallback(
    (incompleteCategorizations: Array<BlockingCategorization>) => {
      dispatch(setIncompleteCategorizations(incompleteCategorizations));

      if (!incompleteCategorizations.length) {
        return;
      }

      const { categorizationId, groupBlockId, index } =
        incompleteCategorizations[0];
      const groupBlockSuffix = groupBlockId ? `-${groupBlockId}` : "";
      const activeField = `categorization-${categorizationId}${groupBlockSuffix}-${index}`;

      dispatch(changeAnnotating(false));
      dispatch(changeActiveEntity(undefined));
      dispatch(changeActiveButton({ id: activeField }));
    },
    [dispatch],
  );

  return useCallback(() => {
    const blockingCategorizations = getBlockingCategorizations();
    if (!blockingCategorizations) {
      return true;
    }

    const incompleteCategorizations: Array<BlockingCategorization> =
      blockingCategorizations.filter(
        (blocker) =>
          !categorizationAnnotations.some(
            (annotation) =>
              annotation.categorizationId === blocker.categorizationId &&
              annotation.groupBlockId === blocker.groupBlockId &&
              annotation.index === blocker.index &&
              !!annotation.value,
          ),
      );

    setIncompleteCategorizationsAndScrollTo(incompleteCategorizations);

    return !incompleteCategorizations.length;
  }, [
    getBlockingCategorizations,
    categorizationAnnotations,
    setIncompleteCategorizationsAndScrollTo,
  ]);
};

export default useValidateBlockingCategorizations;
